/**
 * Estado do módulo de focos (Zustand): fontes, modo de exibição, filtros,
 * estilo, carregamento e dados. Registra o codec de URL `f` no import.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import { indexAreaFires, type AreaMatcher } from './areaFires';
import { KNOWN_SATELLITES, buildDataset, emptyDataset, listSatellites, subsetDataset, type FireDataset } from './dataset';
import type { DisplayMode, FireSource, FireStyle, FireTable, SourceStatus } from './types';

export const REFERENCE_SATELLITE = 'AQUA_M-T';

export const DEFAULT_STYLE: FireStyle = {
  // 4,5 px (era 3,5; 09/10/2026, pedido do usuário): os focos ativos há algum tempo ficavam
  // pequenos ao lado dos pluviômetros, do nível e da qualidade do ar.
  size: 4.5,
  colorGoes: '#ff4f1f',
  colorOthers: '#ffd21f',
  opacity: 0.95,
};

/** Limites dos controles de aparência (painel e link). */
export const STYLE_LIMITS = {
  size: { min: 1.5, max: 8, step: 0.5 },
  opacity: { min: 0.2, max: 1, step: 0.05 },
} as const;

const HEX_COLOR = /^#[0-9a-f]{6}$/i;

function snap(v: number, { min, max, step }: { min: number; max: number; step: number }): number {
  if (!Number.isFinite(v)) return min;
  const clamped = Math.min(max, Math.max(min, v));
  return Math.round(Math.round(clamped / step) * step * 1000) / 1000;
}

/** Garante estilo dentro dos limites do painel (tamanho 1,5–8 px, opacidade 20–100 %, cores #rrggbb). */
export function normalizeStyle(style: FireStyle): FireStyle {
  return {
    size: snap(style.size, STYLE_LIMITS.size),
    opacity: snap(style.opacity, STYLE_LIMITS.opacity),
    colorGoes: HEX_COLOR.test(style.colorGoes) ? style.colorGoes.toLowerCase() : DEFAULT_STYLE.colorGoes,
    colorOthers: HEX_COLOR.test(style.colorOthers) ? style.colorOthers.toLowerCase() : DEFAULT_STYLE.colorOthers,
  };
}

/** Padrão canônico das fontes (é a referência do link compartilhável). */
export const DEFAULT_SOURCES: Record<FireSource, boolean> = { goes10min: true, diario: true };

/** Economia de dados ligada no aparelho (`navigator.connection.saveData`). */
export function prefersSaveData(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData === true;
}

/** Fontes iniciais: com economia de dados, o diário (~3,6 MB por dia) começa desligado. */
export function initialSources(saveData = prefersSaveData()): Record<FireSource, boolean> {
  return { ...DEFAULT_SOURCES, diario: DEFAULT_SOURCES.diario && !saveData };
}
export const DEFAULT_MODE: DisplayMode = 'accum';

export const idleStatus = (state: SourceStatus['state'] = 'idle'): SourceStatus => ({
  state,
  progress: 0,
  message: null,
  error: null,
  errorDetail: null,
  warning: null,
  note: null,
  latest: null,
  publicadoAte: null,
  updatedAt: null,
  bytes: 0,
});

/** Como a Amazônia Legal está sendo filtrada (informado no painel). */
export interface AmazoniaFilterInfo {
  diario: 'municipios' | 'aproximado';
  goes10min: 'poligono' | 'aproximado';
}

/** Situação do filtro por áreas (ver `areaSync.ts`). */
export interface AreaFilterStatus {
  /** none = sem filtro · loading = limites baixando · error = falha ao baixar · ready = aplicado. */
  state: 'none' | 'loading' | 'error' | 'ready';
  /** Áreas (`areaId`) que não existem nos dados: não filtram nada. */
  missing: readonly string[];
}

export const NO_AREA_FILTER: AreaFilterStatus = { state: 'none', missing: [] };

export interface FireState {
  sources: Record<FireSource, boolean>;
  /**
   * Fontes que estavam ligadas quando a camada foi desligada inteira pelo
   * trilho, para religar a mesma seleção. null = camada ligada.
   */
  sourcesBeforeOff: Record<FireSource, boolean> | null;
  mode: DisplayMode;
  /** Satélites ocultos (vazio = todos). Guardar os ocultos, e não os permitidos, faz satélites novos aparecerem. */
  hiddenSatellites: string[];
  onlyAmazonia: boolean;
  style: FireStyle;

  status: Record<FireSource, SourceStatus>;
  /** O aparelho pediu economia de dados (o diário começou desligado). */
  saveData: boolean;
  goes10min: FireTable | null;
  diario: FireTable[];
  amazoniaFilter: AmazoniaFilterInfo;
  /** Satélites presentes nos dados carregados. */
  availableSatellites: string[];
  /**
   * Critérios das áreas do filtro dos "Últimos focos" (null = sem filtro).
   * Com filtro, `dataset` só tem os focos em alguma das áreas.
   */
  areaMatchers: readonly AreaMatcher[] | null;
  areaStatus: AreaFilterStatus;
  /** Focos filtrados e ordenados, prontos para desenhar. */
  dataset: FireDataset;

  setSource: (source: FireSource, on: boolean) => void;
  /**
   * Interruptor do trilho: desliga as duas fontes ou religa a seleção
   * anterior (o padrão, se nenhuma estava ligada).
   */
  setAllSources: (on: boolean) => void;
  setMode: (mode: DisplayMode) => void;
  setHiddenSatellites: (hidden: string[]) => void;
  toggleSatellite: (satellite: string) => void;
  /** Mostra só um satélite (ex.: o de referência) — oculta os demais conhecidos. */
  showOnlySatellite: (satellite: string) => void;
  setOnlyAmazonia: (on: boolean) => void;
  setStyle: (patch: Partial<FireStyle>) => void;
  resetStyle: () => void;

  /** Uso interno (sync.ts). */
  setStatus: (source: FireSource, patch: Partial<SourceStatus>) => void;
  setData: (patch: { goes10min?: FireTable | null; diario?: FireTable[] }) => void;
  setAmazoniaFilter: (patch: Partial<AmazoniaFilterInfo>) => void;
  /** Uso interno (areaSync.ts). `matchers` undefined = mantém os critérios atuais. */
  setAreaFilter: (matchers: readonly AreaMatcher[] | null | undefined, status: AreaFilterStatus) => void;
}

function derive(
  s: Pick<FireState, 'goes10min' | 'diario' | 'sources' | 'onlyAmazonia' | 'hiddenSatellites' | 'areaMatchers'>,
) {
  const all = buildDataset(
    { goes10min: s.goes10min, diario: s.diario },
    { sources: s.sources, onlyAmazonia: s.onlyAmazonia, hidden: new Set(s.hiddenSatellites) },
  );
  // Filtro por áreas: uma passada pelo conjunto (poucos ms), só quando dados, filtros ou áreas mudam.
  return { dataset: s.areaMatchers ? subsetDataset(all, indexAreaFires(all, s.areaMatchers).idx) : all };
}

export const useFireStore = create<FireState>()((set, get) => {
  /** Aplica a mudança e recalcula o conjunto de focos. */
  const update = (patch: Partial<FireState>) => {
    const next = { ...get(), ...patch };
    set({ ...patch, ...derive(next) });
  };

  const saveData = prefersSaveData();
  return {
    sources: initialSources(saveData),
    sourcesBeforeOff: null,
    saveData,
    mode: DEFAULT_MODE,
    hiddenSatellites: [],
    onlyAmazonia: true,
    style: { ...DEFAULT_STYLE },
    status: { goes10min: idleStatus(), diario: idleStatus() },
    goes10min: null,
    diario: [],
    amazoniaFilter: { diario: 'aproximado', goes10min: 'aproximado' },
    availableSatellites: [],
    areaMatchers: null,
    areaStatus: NO_AREA_FILTER,
    dataset: emptyDataset(),

    setSource: (source, on) => {
      if (get().sources[source] === on) return;
      update({ sources: { ...get().sources, [source]: on } });
    },
    setAllSources: (on) => {
      const s = get();
      const anyOn = s.sources.goes10min || s.sources.diario;
      if (!on) {
        if (anyOn) update({ sources: { goes10min: false, diario: false }, sourcesBeforeOff: s.sources });
        return;
      }
      if (anyOn) return;
      const before = s.sourcesBeforeOff;
      const restore = before && (before.goes10min || before.diario) ? before : initialSources(s.saveData);
      update({ sources: { ...restore }, sourcesBeforeOff: null });
    },
    setMode: (mode) => set({ mode }),
    setHiddenSatellites: (hidden) => update({ hiddenSatellites: [...new Set(hidden)].sort() }),
    toggleSatellite: (sat) => {
      const hidden = new Set(get().hiddenSatellites);
      if (hidden.has(sat)) hidden.delete(sat);
      else hidden.add(sat);
      update({ hiddenSatellites: [...hidden].sort() });
    },
    showOnlySatellite: (sat) => {
      const all = new Set<string>([...KNOWN_SATELLITES, ...get().availableSatellites]);
      all.delete(sat);
      update({ hiddenSatellites: [...all].sort() });
    },
    setOnlyAmazonia: (onlyAmazonia) => update({ onlyAmazonia }),
    setStyle: (patch) => set({ style: normalizeStyle({ ...get().style, ...patch }) }),
    resetStyle: () => set({ style: { ...DEFAULT_STYLE } }),

    setStatus: (source, patch) => set({ status: { ...get().status, [source]: { ...get().status[source], ...patch } } }),
    setData: (patch) => {
      const next = { ...get(), ...patch };
      update({ ...patch, availableSatellites: listSatellites({ goes10min: next.goes10min, diario: next.diario }) });
    },
    setAmazoniaFilter: (patch) => set({ amazoniaFilter: { ...get().amazoniaFilter, ...patch } }),
    setAreaFilter: (matchers, areaStatus) => {
      if (matchers === undefined || matchers === get().areaMatchers) set({ areaStatus });
      else update({ areaMatchers: matchers, areaStatus });
    },
  };
});

// ---------------------------------------------------------------------------
// Link compartilhável — chave `f`
// ---------------------------------------------------------------------------
//
// Tokens separados por ".", só os que diferem do padrão (caracteres que o
// URLSearchParams não codifica: letras, dígitos, "-", "_", "*", "."):
//   g0/g1   GOES-19 10 min desligado/ligado     d0/d1  diário
//   mq/ma/mt modo: quadro atual / acumulado / todo o período
//   a0/a1   somente Amazônia Legal
//   z35     tamanho ×10 (3,5 px)                o80    opacidade (%)
//   cRRGGBBRRGGBB  cores (GOES-19, outros)
//   xMSG-03*NOAA-20  satélites OCULTOS (separados por "*"); nomes
//                    desconhecidos não escondem nada
//   sAQUA_M-T*…      (formato antigo) satélites PERMITIDOS — lido e convertido
//                    em ocultos pela lista de satélites conhecidos; nomes
//                    desconhecidos são ignorados

type UrlState = Pick<FireState, 'sources' | 'mode' | 'hiddenSatellites' | 'onlyAmazonia' | 'style'>;

const SAT_NAME = /^[A-Za-z0-9_-]{1,20}$/;

const MODE_CODES: Record<DisplayMode, string> = { frame: 'q', accum: 'a', all: 't' };
const HEX6 = /^[0-9a-f]{6}$/i;

export function encodeFireUrl(s: UrlState): string | null {
  const tokens: string[] = [];
  if (s.sources.goes10min !== DEFAULT_SOURCES.goes10min) tokens.push(`g${s.sources.goes10min ? 1 : 0}`);
  if (s.sources.diario !== DEFAULT_SOURCES.diario) tokens.push(`d${s.sources.diario ? 1 : 0}`);
  if (s.mode !== DEFAULT_MODE) tokens.push(`m${MODE_CODES[s.mode]}`);
  if (!s.onlyAmazonia) tokens.push('a0');
  if (s.style.size !== DEFAULT_STYLE.size) tokens.push(`z${Math.round(s.style.size * 10)}`);
  if (s.style.opacity !== DEFAULT_STYLE.opacity) tokens.push(`o${Math.round(s.style.opacity * 100)}`);
  if (s.style.colorGoes !== DEFAULT_STYLE.colorGoes || s.style.colorOthers !== DEFAULT_STYLE.colorOthers) {
    tokens.push(`c${s.style.colorGoes.slice(1)}${s.style.colorOthers.slice(1)}`.toLowerCase());
  }
  const hidden = s.hiddenSatellites.filter((x) => SAT_NAME.test(x));
  if (hidden.length) tokens.push(`x${hidden.join('*')}`);
  return tokens.length ? tokens.join('.') : null;
}

export function decodeFireUrl(value: string): Partial<UrlState> {
  const out: Partial<UrlState> = {};
  const sources = { ...DEFAULT_SOURCES };
  const style = { ...DEFAULT_STYLE };
  let touchedSources = false;
  let touchedStyle = false;
  for (const tok of String(value).split('.')) {
    const k = tok.charAt(0);
    const v = tok.slice(1);
    if ((k === 'g' || k === 'd') && (v === '0' || v === '1')) {
      sources[k === 'g' ? 'goes10min' : 'diario'] = v === '1';
      touchedSources = true;
    } else if (k === 'm') {
      const mode = (Object.keys(MODE_CODES) as DisplayMode[]).find((m) => MODE_CODES[m] === v);
      if (mode) out.mode = mode;
    } else if (k === 'a' && (v === '0' || v === '1')) {
      out.onlyAmazonia = v === '1';
    } else if (k === 'z' && /^\d{1,3}$/.test(v)) {
      style.size = Number(v) / 10;
      touchedStyle = true;
    } else if (k === 'o' && /^\d{1,3}$/.test(v)) {
      style.opacity = Number(v) / 100;
      touchedStyle = true;
    } else if (k === 'c' && v.length === 12 && HEX6.test(v.slice(0, 6)) && HEX6.test(v.slice(6))) {
      style.colorGoes = `#${v.slice(0, 6).toLowerCase()}`;
      style.colorOthers = `#${v.slice(6).toLowerCase()}`;
      touchedStyle = true;
    } else if (k === 'x') {
      out.hiddenSatellites = [...new Set(v.split('*').filter((x) => SAT_NAME.test(x)))].sort();
    } else if (k === 's') {
      // Formato antigo (permitidos): converte em ocultos; ignora nomes desconhecidos.
      const known = new Set<string>(KNOWN_SATELLITES);
      const allowed = v.split('*').filter((x) => known.has(x));
      if (allowed.length) out.hiddenSatellites = KNOWN_SATELLITES.filter((x) => !allowed.includes(x)).sort();
    }
  }
  if (touchedSources) out.sources = sources;
  if (touchedStyle) out.style = normalizeStyle(style);
  return out;
}

registerUrlCodec({
  key: 'f',
  encode: () => encodeFireUrl(useFireStore.getState()),
  decode: (value) => {
    const patch = decodeFireUrl(value);
    const s = useFireStore.getState();
    if (patch.sources) (Object.keys(patch.sources) as FireSource[]).forEach((k) => s.setSource(k, patch.sources![k]));
    if (patch.mode) s.setMode(patch.mode);
    if (patch.onlyAmazonia !== undefined) s.setOnlyAmazonia(patch.onlyAmazonia);
    if (patch.hiddenSatellites !== undefined) s.setHiddenSatellites(patch.hiddenSatellites);
    if (patch.style) s.setStyle(patch.style);
  },
  subscribe: (onChange) =>
    useFireStore.subscribe((s, prev) => {
      if (
        s.sources !== prev.sources ||
        s.mode !== prev.mode ||
        s.hiddenSatellites !== prev.hiddenSatellites ||
        s.onlyAmazonia !== prev.onlyAmazonia ||
        s.style !== prev.style
      ) {
        onChange();
      }
    }),
});
